import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Moon, Sun, Menu, X, ChevronDown } from 'lucide-react';
import { cityDropdownLinks } from '../data/fuelPricePages';

const Layout = ({ children, darkMode, toggleDarkMode, lang = 'pl' }) => {
  const [mobileMenuOpen, setMobileMenuOpen]       = useState(false);
  const [pricesOpen, setPricesOpen]               = useState(false);
  const [mobilePricesOpen, setMobilePricesOpen]   = useState(false);
  const dropdownRef = useRef(null);

  const isPL          = lang === 'pl';
  const homeLink      = isPL ? '/'    : '/en';
  const blogLink      = isPL ? '/blog': '/en/blog';
  const gallonsLink   = isPL ? '/przelicznik-galony-na-litry' : '/en/gallons-to-liters-converter';
  const litersLink    = isPL ? '/przelicznik-litry-na-galony' : '/en/liters-to-gallons-converter';
  const langLink      = isPL ? '/en'  : '/';
  const langLabel     = isPL ? 'EN'   : 'PL';

  const dm = darkMode;
  const bg        = dm ? 'bg-[#09090B]'     : 'bg-white';
  const border    = dm ? 'border-[#27272A]' : 'border-[#5B5B5B]';
  const navBg     = dm ? 'bg-[#09090B]/95 border-[#27272A]' : 'bg-white/95 border-[#5B5B5B]';
  const textSec   = dm ? 'text-[#71717A]'   : 'text-[#2E3137]';
  const textPri   = dm ? 'text-[#F9FAFB]'   : 'text-[#111827]';
  const cardBg    = dm ? 'bg-[#18181B]'     : 'bg-white';

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setPricesOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const navLinkCls = `text-sm font-medium ${textSec} hover:text-accent underline-offset-4 hover:underline transition-colors`;

  return (
    <div className={`min-h-screen ${bg} font-sans transition-colors duration-300`}>

      {/* ─── HEADER ────────────────────────────────────────────────── */}
      <header className={`${navBg} border-b fixed top-0 w-full z-20 backdrop-blur-md`}>
        <div className="max-w-content mx-auto px-6 h-14 flex justify-between items-center">

          <Link to={homeLink} className="font-bold text-lg tracking-tight text-accent">
            Galony&amp;Litry
          </Link>

          {/* desktop nav */}
          <nav className="hidden md:flex items-center gap-7">
            <Link to={gallonsLink} className={navLinkCls}>
              {isPL ? 'Galony → Litry' : 'Gallons → Liters'}
            </Link>
            <Link to={litersLink} className={navLinkCls}>
              {isPL ? 'Litry → Galony' : 'Liters → Gallons'}
            </Link>

            {isPL && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setPricesOpen(o => !o)}
                  className={`flex items-center gap-1 text-sm font-medium ${textSec} hover:text-accent transition-colors`}
                >
                  Ceny paliw
                  <ChevronDown size={13} className={`transition-transform duration-200 ${pricesOpen ? 'rotate-180' : ''}`} />
                </button>
                {pricesOpen && (
                  <div className={`absolute top-full left-0 mt-2 w-52 rounded-2xl border shadow-lg py-1.5 z-50 ${cardBg} ${border} animate-fade-in`}>
                    {cityDropdownLinks.map(({ label, path }) => (
                      <Link key={path} to={path}
                        onClick={() => setPricesOpen(false)}
                        className={`block px-4 py-2 text-sm ${textSec} hover:text-accent transition-colors`}>
                        {label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            <Link to={blogLink} className={navLinkCls}>Blog</Link>
          </nav>

          {/* right controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(o => !o)}
              className={`md:hidden p-1.5 rounded-lg ${textSec} hover:text-accent transition-colors`}>
              {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <Link to={langLink}
              className={`text-sm font-medium px-2.5 py-1 rounded-lg border ${border} ${textSec} hover:text-accent hover:border-accent transition-colors`}>
              {langLabel}
            </Link>
            <button onClick={toggleDarkMode}
              className={`p-1.5 rounded-lg ${textSec} hover:text-accent transition-colors`}>
              {dm ? <Sun size={16} /> : <Moon size={16} />}
            </button>
          </div>
        </div>

        {/* mobile menu */}
        {mobileMenuOpen && (
          <div className={`md:hidden border-t ${border} ${dm ? 'bg-[#09090B]' : 'bg-white'}`}>
            <div className="max-w-content mx-auto px-6 flex flex-col py-2">
              <Link to={gallonsLink} onClick={() => setMobileMenuOpen(false)}
                className={`text-sm font-medium py-3 border-b ${border} ${textSec}`}>
                {isPL ? 'Galony → Litry' : 'Gallons → Liters'}
              </Link>
              <Link to={litersLink} onClick={() => setMobileMenuOpen(false)}
                className={`text-sm font-medium py-3 border-b ${border} ${textSec}`}>
                {isPL ? 'Litry → Galony' : 'Liters → Gallons'}
              </Link>

              {isPL && (
                <>
                  <button onClick={() => setMobilePricesOpen(o => !o)}
                    className={`text-sm font-medium py-3 border-b ${border} ${textSec} flex items-center justify-between`}>
                    Ceny paliw
                    <ChevronDown size={13} className={`transition-transform ${mobilePricesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {mobilePricesOpen && cityDropdownLinks.map(({ label, path }) => (
                    <Link key={path} to={path}
                      onClick={() => { setMobileMenuOpen(false); setMobilePricesOpen(false); }}
                      className={`text-sm py-2.5 pl-4 border-b ${border} ${textSec}`}>
                      {label}
                    </Link>
                  ))}
                </>
              )}

              <Link to={blogLink} onClick={() => setMobileMenuOpen(false)}
                className={`text-sm font-medium py-3 ${textSec}`}>
                Blog
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ─── MAIN ──────────────────────────────────────────────────── */}
      <main className="pt-14 min-h-screen">
        {children}
      </main>

      {/* ─── FOOTER ────────────────────────────────────────────────── */}
      <footer className={`${dm ? 'bg-[#18181B] border-[#27272A]' : 'bg-[#374151] border-[#4B5563]'} border-t py-8 px-4`}>
        <div className="max-w-content mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="font-bold text-sm text-accent">Galony&amp;Litry.pl</span>
          <p className="text-xs text-[#D1D5DB]">
            © 2026 Galony&amp;Litry.pl · {isPL ? 'Wszystkie przeliczniki mają charakter informacyjny' : 'All calculators are for informational purposes only'}
          </p>
          <div className="flex flex-col md:flex-row items-center gap-3 text-xs text-[#9CA3AF]">
            <Link to={langLink} className="hover:text-accent transition-colors">
              {isPL ? 'English version →' : 'Wersja polska →'}
            </Link>
            <span className="hidden md:inline text-[#6B7280]">·</span>
            <span>
              Projekt i kodowanie:{' '}
              <a href="https://crowbox.pl" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">Crowbox.pl</a>
              {' '}&amp;{' '}
              <a href="https://reklamaprawnika.pl" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">Reklamaprawnika.pl</a>
            </span>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default Layout;
